import { computed, onScopeDispose, reactive, ref, unref } from "vue";
import type { ComputedRef, Ref } from "vue";
import { useI18n } from "vue-i18n";

import { masterApi, masterFullName } from "@/entities/master";
import type { Master } from "@/entities/master";
import {
  mapMasterServiceError,
  masterServiceApi,
  validateServiceForm,
} from "@/entities/master-service";
import type { MasterServiceConfigInput } from "@/entities/master-service";
import type { AppError } from "@/shared/api";
import { isAppError } from "@/shared/api";
import { parseAmountToCents, toast } from "@/shared/lib";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AssignedMaster {
  masterId: string;
  name: string;
  durationMin: number;
  priceCents: number;
  bufferMin: number;
}

export type AssignStep = "detail" | "picker" | "config";

export interface InlineCreateState {
  open: boolean
  name: string
  phone: string
  status: "idle" | "saving"
  error?: string
}

export type DetachOutcome = "success" | "blocked" | "error";

export interface UseServiceAssignResult {
  step: Ref<AssignStep>
  assignedStatus: Ref<"idle" | "loading" | "ready" | "error">
  assigned: Ref<AssignedMaster[]>
  fromPriceCents: Ref<number | null>
  fromDurationMin: Ref<number | null>
  /** Selected master in the picker step. */
  pickerSelectedMasterId: Ref<string | null>
  pickerSearch: Ref<string>
  /** Show search input when candidates >= 6. */
  showSearch: ComputedRef<boolean>
  /** Roster minus already-assigned, optionally filtered by pickerSearch. */
  candidates: ComputedRef<{ masterId: string; name: string }[]>
  /** Config form values (raw strings). */
  config: {
    duration: string
    price: string
    buffer: string
    status: "idle" | "saving"
    fieldErrors: Partial<Record<"duration" | "price" | "buffer", string>>
    formError?: string
  }
  /** True when stale (service gone) — parent should close the assign sheet. */
  staleClose: Ref<boolean>
  successBanner: Ref<boolean>
  /** Name of master shown in success banner. */
  successBannerMasterName: Ref<string>
  /** Inline create form state (T7). */
  inlineCreate: InlineCreateState
  loadAssigned(serviceId: string): Promise<void>
  saveConfig(): Promise<void>
  detach(masterId: string): Promise<DetachOutcome>
  goToPicker(): void
  goToConfig(masterId: string): void
  cancelConfig(): void
  backToDetail(): void
  openInlineCreate(): void
  createMasterInline(): Promise<void>
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BANNER_DISMISS_MS = 3000;

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

/**
 * Оркестрація флоу «призначення майстра до послуги» (spec §4.2, 169-T3).
 * Приймає `roster` як реактивний Ref — джерело імен для join.
 * Стейт-машина: detail ↔ picker ↔ config.
 * W4/W5: таймер банера очищується через `onScopeDispose` та перед кожним
 * новим запуском.
 */
export function useServiceAssign(
  roster: Ref<Master[]>,
  onMutationSuccess?: () => void | Promise<void>,
): UseServiceAssignResult {
  // useI18n() requires a component context. When the composable is called
  // directly in unit tests (outside a component setup), we fall back to
  // returning the key as-is. In production it always runs inside a component.
  let tFn: (key: string) => string = (key: string) => key;
  try {
    const { t } = useI18n();
    tFn = t;
  } catch {
    // no-op — outside component context (e.g. unit tests without mounting)
  }

  function tr(key: string): string {
    return tFn(key);
  }
  // ── Core state ────────────────────────────────────────────────────────────
  const step = ref<AssignStep>("detail");
  const assignedStatus = ref<"idle" | "loading" | "ready" | "error">("idle");
  const assigned = ref<AssignedMaster[]>([]);
  const fromPriceCents = ref<number | null>(null);
  const fromDurationMin = ref<number | null>(null);
  const staleClose = ref(false);
  const successBanner = ref(false);
  const successBannerMasterName = ref("");

  // ── Inline create state (T7) ──────────────────────────────────────────────
  const inlineCreate = reactive<InlineCreateState>({
    open: false,
    name: "",
    phone: "",
    status: "idle",
    error: undefined,
  });

  /** Internally stored serviceId after loadAssigned call. */
  let currentServiceId: string | null = null;

  // ── Picker state ──────────────────────────────────────────────────────────
  const pickerSelectedMasterId = ref<string | null>(null);
  const pickerSearch = ref("");

  const candidates = computed<{ masterId: string; name: string }[]>(() => {
    const assignedIds = new Set(assigned.value.map((a) => a.masterId));
    const search = pickerSearch.value.trim().toLowerCase();
    return unref(roster)
      .filter((m) => !assignedIds.has(m.id))
      .filter((m) => !search || masterFullName(m).toLowerCase().includes(search))
      .map((m) => ({ masterId: m.id, name: masterFullName(m) }));
  });

  const showSearch = computed(() => {
    const assignedIds = new Set(assigned.value.map((a) => a.masterId));
    return unref(roster).filter((m) => !assignedIds.has(m.id)).length >= 6;
  });

  // ── Config form state ─────────────────────────────────────────────────────
  const config = reactive({
    duration: "",
    price: "",
    buffer: "",
    status: "idle" as "idle" | "saving",
    fieldErrors: {} as Partial<Record<"duration" | "price" | "buffer", string>>,
    formError: undefined as string | undefined,
  });

  // ── Banner timer (W4/W5) ───────────────────────────────────────────────────
  let bannerTimer: ReturnType<typeof setTimeout> | null = null;

  function clearBannerTimer(): void {
    if (bannerTimer !== null) {
      clearTimeout(bannerTimer);
      bannerTimer = null;
    }
  }

  function armBannerTimer(): void {
    clearBannerTimer(); // W5: clear previous before arming new
    bannerTimer = setTimeout(() => {
      successBanner.value = false;
      bannerTimer = null;
    }, BANNER_DISMISS_MS);
  }

  onScopeDispose(() => {
    clearBannerTimer(); // W4: cleanup on unmount
  });

  // ── Roster join helper ────────────────────────────────────────────────────
  function joinName(masterId: string): string {
    const master = unref(roster).find((m) => m.id === masterId);
    return master ? masterFullName(master) : tr("masters.services.unknownMaster");
  }

  // ── loadAssigned ──────────────────────────────────────────────────────────
  async function loadAssigned(serviceId: string): Promise<void> {
    currentServiceId = serviceId;
    assignedStatus.value = "loading";

    try {
      const result = await masterServiceApi.listServiceMasters(serviceId);

      assigned.value = result.masters.map((link) => ({
        masterId: link.masterId,
        name: joinName(link.masterId),
        durationMin: link.durationMin,
        priceCents: link.priceCents,
        bufferMin: link.bufferMin,
      }));

      fromPriceCents.value = result.fromPriceCents;
      fromDurationMin.value = result.fromDurationMin;
      assignedStatus.value = "ready";
    } catch {
      assignedStatus.value = "error";
    }
  }

  // ── saveConfig ────────────────────────────────────────────────────────────
  async function saveConfig(): Promise<void> {
    if (config.status === "saving") return;
    if (!currentServiceId || !pickerSelectedMasterId.value) return;

    config.status = "saving";
    config.fieldErrors = {};
    config.formError = undefined;

    // Client-side validation — blocks the PUT for invalid duration/price/buffer
    const formErrors = validateServiceForm(
      { serviceId: currentServiceId, duration: config.duration, price: config.price, buffer: config.buffer },
      "edit",
    );
    if (formErrors.duration) config.fieldErrors.duration = formErrors.duration;
    if (formErrors.price) config.fieldErrors.price = formErrors.price;
    if (formErrors.buffer) config.fieldErrors.buffer = formErrors.buffer;
    if (Object.keys(config.fieldErrors).length > 0) {
      config.status = "idle";
      return;
    }

    const durationMin = parseInt(config.duration, 10) || 0;
    const rawCents = parseAmountToCents(config.price);
    const priceCents = isNaN(rawCents) ? 0 : rawCents;
    const bufferMin = config.buffer === "" ? 0 : (parseInt(config.buffer, 10) || 0);

    const input: MasterServiceConfigInput = { durationMin, priceCents, bufferMin };

    try {
      const link = await masterServiceApi.setServiceMaster(
        currentServiceId,
        pickerSelectedMasterId.value,
        input,
      );

      // Server-confirmed reconcile: update or add to assigned
      const updatedMaster: AssignedMaster = {
        masterId: link.masterId,
        name: joinName(link.masterId),
        durationMin: link.durationMin,
        priceCents: link.priceCents,
        bufferMin: link.bufferMin,
      };

      const idx = assigned.value.findIndex((a) => a.masterId === link.masterId);
      if (idx >= 0) {
        assigned.value[idx] = updatedMaster;
      } else {
        assigned.value.push(updatedMaster);
      }

      // Update aggregate hints
      if (fromPriceCents.value === null || link.priceCents < fromPriceCents.value) {
        fromPriceCents.value = link.priceCents;
      }

      successBannerMasterName.value = joinName(link.masterId);
      successBanner.value = true;
      armBannerTimer(); // W4/W5

      step.value = "detail";
      pickerSelectedMasterId.value = null;

      void onMutationSuccess?.();
    } catch (err) {
      const appErr: AppError = isAppError(err) ? err : { kind: "server", retriable: true };
      const mapped = mapMasterServiceError(appErr, "submit");

      switch (mapped.surface) {
        case "stale":
          staleClose.value = true;
          void loadAssigned(currentServiceId);
          break;
        case "notFound":
          staleClose.value = true;
          break;
        case "field":
          if (mapped.field && (mapped.field === "duration" || mapped.field === "price" || mapped.field === "buffer")) {
            config.fieldErrors[mapped.field] = mapped.i18nKey;
          }
          break;
        case "toast":
          toast.error(tr(mapped.i18nKey));
          break;
        default:
          config.formError = mapped.i18nKey;
      }
    } finally {
      config.status = "idle";
    }
  }

  // ── detach ────────────────────────────────────────────────────────────────
  async function detach(masterId: string): Promise<DetachOutcome> {
    if (!currentServiceId) return "error";

    try {
      await masterServiceApi.removeServiceMaster(currentServiceId, masterId);
      const idx = assigned.value.findIndex((a) => a.masterId === masterId);
      if (idx >= 0) {
        assigned.value.splice(idx, 1);
      }
      // Return to detail list after successful detach (spec §7.2)
      step.value = "detail";
      pickerSelectedMasterId.value = null;
      config.status = "idle";
      config.fieldErrors = {};
      config.formError = undefined;

      void onMutationSuccess?.();
      return "success";
    } catch (err) {
      const appErr: AppError = isAppError(err) ? err : { kind: "server", retriable: true };
      // 409 conflict: master has active bookings — caller renders blocked state, no toast
      if (appErr.kind === "conflict") {
        return "blocked";
      }
      const mapped = mapMasterServiceError(appErr, "detach");
      if (mapped.surface === "toast") {
        toast.error(tr(mapped.i18nKey));
      }
      return "error";
    }
  }

  // ── Step transitions ──────────────────────────────────────────────────────
  function goToPicker(): void {
    step.value = "picker";
    pickerSearch.value = "";
  }

  function goToConfig(masterId: string): void {
    pickerSelectedMasterId.value = masterId;
    step.value = "config";
    // Pre-fill from existing assignment if editing
    const existing = assigned.value.find((a) => a.masterId === masterId);
    config.duration = existing ? String(existing.durationMin) : "";
    config.price = existing ? (existing.priceCents / 100).toFixed(2) : "";
    config.buffer = existing ? String(existing.bufferMin) : "";
    config.status = "idle";
    config.fieldErrors = {};
    config.formError = undefined;
  }

  function cancelConfig(): void {
    step.value = "picker";
    pickerSelectedMasterId.value = null;
  }

  function backToDetail(): void {
    step.value = "detail";
    pickerSelectedMasterId.value = null;
    pickerSearch.value = "";
  }

  // ── Inline create (T7) ────────────────────────────────────────────────────
  function openInlineCreate(): void {
    inlineCreate.open = true;
    inlineCreate.name = "";
    inlineCreate.phone = "";
    inlineCreate.status = "idle";
    inlineCreate.error = undefined;
  }

  async function createMasterInline(): Promise<void> {
    if (inlineCreate.status === "saving") return;
    inlineCreate.status = "saving";
    inlineCreate.error = undefined;
    try {
      const newMaster = await masterApi.createMaster({
        firstName: inlineCreate.name.trim(),
        lastName: "",
        middleName: "",
        email: "",
        phone: inlineCreate.phone.trim(),
      });
      unref(roster).push(newMaster);
      inlineCreate.open = false;
      goToConfig(newMaster.id);
    } catch {
      inlineCreate.error = "masters.errors.createFailed";
    } finally {
      inlineCreate.status = "idle";
    }
  }

  return {
    step,
    assignedStatus,
    assigned,
    fromPriceCents,
    fromDurationMin,
    pickerSelectedMasterId,
    pickerSearch,
    showSearch,
    candidates,
    config,
    staleClose,
    successBanner,
    successBannerMasterName,
    inlineCreate,
    loadAssigned,
    saveConfig,
    detach,
    goToPicker,
    goToConfig,
    cancelConfig,
    backToDetail,
    openInlineCreate,
    createMasterInline,
  };
}
